import React, { useContext, useEffect, useRef, useState } from 'react';
import { DatePicker, Row, Col, Checkbox, Button, Dropdown, Menu, Switch, Card, Badge, Modal } from 'antd';
import './PerformanceDashboard.css';
import useApi from '../../hooks/useApi';
import metricApiList from '../../api/PerformanceMetricsApi';
import MetricCard from './MetricCard';
import MetricDropdown from './Metric Breakdown/MetricDropDown';
import { MyContext } from '../../context/myContext';
import MetricChartDropdown from './Trend Analysis/MetricChartDropdown';
import moment, { Moment } from 'moment';
import { HiViewColumns } from "react-icons/hi2";
import { DownloadOutlined, EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import ProductDropdown from './productsDropdown';
import { getProductList } from '../../utils/DataService';
import { ChartType, Metric, StackingType } from '../../interfaces/IMetricChart';
import { ALL } from '../Release Comparison/ReleaseComparison';
import DropdownSection from '../../utils/DropDownSection';
import releaseDetialsApiList from '../../api/ReleaseDetailsApi';
import { PageType } from '../../utils/AppEnum';

type Dataset = {
    set: string;
    value: number;
};

export enum BreakdownType {
    CURRENT_RELEASE = "current_release",
    ALL_RELEASES = "all_releases",
}

export type WorkItemType = {
    count: number;
    datasets: Dataset[];
    value: number;
    type: string;
};

const getUniqueTypes = (data: any[]) => {
    const types = new Set<string>();

    if (Array.isArray(data)) {
        data.forEach((metric) => {
            if (Array.isArray(metric.data)) {
                metric.data.forEach((item: { work_item_types: any[]; }) => {
                    if (Array.isArray(item.work_item_types)) {
                        item.work_item_types.forEach((workItem: { type: string }) => {
                            if (
                                typeof workItem?.type === "string" &&
                                workItem.type.trim() !== "" &&
                                workItem.type.toLowerCase() !== "null" &&
                                workItem.type.toLowerCase() !== "undefined" &&
                                !Number.isNaN(Number(workItem.type)) === false
                            ) {
                                types.add(workItem.type);
                            }
                        });
                    }
                });
            }
        });
    }
    return Array.from(types);
};

const getStateCategories = (metrics: any[]): string[] => {
    if (!Array.isArray(metrics) || metrics.length === 0) {
        return [];
    }

    const allSets = new Set<string>();

    metrics[0]?.data?.forEach((dataItem: any) => {
        dataItem.work_item_types?.forEach((type: any) => {
            type.datasets?.forEach((dataset: any) => {
                if (dataset.set) {
                    allSets.add(dataset.set);
                }
            });
        });
    });

    return Array.from(allSets);
};

export const calculateAverage = (data: any[], weight: string | null): number => {
    let grandTotalSum = 0;
    let grandTotalCount = 0;

    data.forEach((entry: { work_item_types: WorkItemType[] }) => {
        let totalValueForRelease = 0;
        let totalCountForRelease = 0;

        if (Array.isArray(entry.work_item_types)) {
            entry.work_item_types.forEach(({ count, datasets, value }) => {
                let totalValueForType = 0;

                const hasValidDatasets = Array.isArray(datasets) && datasets.length > 0;

                if (weight !== null) {
                    if (hasValidDatasets) {
                        datasets.forEach(({ value }) => {
                            totalValueForType += value;
                        });
                    } else if (typeof value === "number") {
                        totalValueForType = value;
                    }
                    totalValueForType *= count;
                } else {
                    if (hasValidDatasets) {
                        datasets.forEach(({ value }) => {
                            totalValueForType += value;
                        });
                    } else if (typeof value === "number") {
                        totalValueForType = value;
                    }
                }

                totalValueForRelease += totalValueForType;
                totalCountForRelease += count;
            });
        }

        grandTotalSum += totalValueForRelease;

        if (weight !== null) {
            grandTotalCount += totalCountForRelease;
        }
    });

    if (weight === null) {
        return data.length ? parseFloat((grandTotalSum / data.length).toFixed(1)) : 0;
    }

    return grandTotalCount ? parseFloat((grandTotalSum / grandTotalCount).toFixed(1)) : 0;
};


const getFilterStorageKey = (product: string) => `performanceFilters_${product}`;
const getVisibiltyStorageKey = (product: string) => `metric_summary_visibility_${product}`;

const Performance: React.FC = () => {
    const getMetricApi = useApi(metricApiList.getMetrics)
    const getPerformanceInsightsApi = useApi(metricApiList.getPerformanceInsights)
    const getPerformanceProblemWorkItemsApi = useApi(metricApiList.getPerformanceProblemWorkItems)
    const getReleaseDetailsApi = useApi(releaseDetialsApiList.getReleaseDetails)
    const [performanceMetricsData, setPerformanceMetricsData] = useState<any>([]);
    const [releases, setReleases] = useState<string[]>([]);
    const [isWarningModalVisible, setIsWarningModalVisible] = useState(false);
    const [pendingMetricToggle, setPendingMetricToggle] = useState<string | null>(null);

    const { metricData, setMetricData, setLoading, setVisibility, visibility, setWorkItemClicked, setSelectedInsightWorkItems, selectedProduct, setIsJourneyView, isTargetEnabled, setIsTargetEnabled, stateCategories, setStateCategories, selectedStateCategory, setSelectedStateCategory, setReleaseDetials, isCompareTargetVisible, setIsCompareTargetVisible } = useContext<any>(MyContext);
    const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
    const [selectedReleases, setSelectedReleases] = useState<string[]>([]);
    const [selectedStartDate, setSelectedStartDate] = useState<any>(null);
    const [selectedEndDate, setSelectedEndDate] = useState<any>(null);
    const [insights, setInsights] = useState<any>(null);
    const [filteredInsights, setFilteredInsights] = useState<any>(null);
    const [problemWorkItems, setProblemWorkItems] = useState<any>(null);
    const [filteredProblemWorkItems, setFilteredProblemWorkItems] = useState<any>(null);
    const [workItemTypes, setWorkItemTypes] = useState<string[]>([]);
    const [selectedType, setSelectedType] = useState<string>(ALL);
    const [selectedMetric, setSelectedMetric] = useState<string | undefined>(metricData[0]?.name);
    const [averageValue, setAverageValue] = useState<string>('0');
    const [selectedMetricY1, setSelectedMetricY1] = useState<string | undefined>(metricData[0]?.name);
    const [selectedMetricY2, setSelectedMetricY2] = useState<string | undefined>(metricData[1]?.name);
    const [selectedBreakdownType, setSelectedBreakdownType] = useState<string>(BreakdownType.CURRENT_RELEASE)
    const [selectedTypeY1, setSelectedTypeY1] = useState('line');
    const [selectedTypeY2, setSelectedTypeY2] = useState('bar');
    const [selectedStackingY1, setSelectedStackingY1] = useState("Simple");
    const [selectedStackingY2, setSelectedStackingY2] = useState("Simple");
    const [selectedMetricCard, setSelectedMetricCard] = useState<string | undefined>();
    const [rollingWindowSizeY1, setRollingWindowSizeY1] = useState(1);
    const [rollingWindowSizeY2, setRollingWindowSizeY2] = useState(1);
    const navigate = useNavigate();
    const productList = JSON.parse(getProductList()) || [];
    const hasFetchedFilters = useRef(false);

    const [filters, setFilters] = useState(() => {
        const storedFilters = localStorage.getItem(getFilterStorageKey(selectedProduct));
        return storedFilters
            ? JSON.parse(storedFilters)
            : {
                startDate: selectedStartDate,
                endDate: selectedStartDate,
                releases: selectedReleases,
                workItemType: selectedType,
                breakDownType: BreakdownType.CURRENT_RELEASE,
                releaseBreakdown: metricData[0]?.name,
                analysisA: metricData[0]?.name,
                analysisB: metricData[1]?.name,
                selectedTypeY1: selectedTypeY1,
                selectedTypeY2: selectedTypeY2,
                selectedStackingY1: selectedStackingY1,
                selectedStackingY2: selectedStackingY2,
                rollingWindowSizeY1: rollingWindowSizeY1,
                rollingWindowSizeY2: rollingWindowSizeY2,
            };
    });

    useEffect(() => {
        hasFetchedFilters.current = false;

        const storedFilters = localStorage.getItem(getFilterStorageKey(selectedProduct));
        if (storedFilters) {
            setFilters(JSON.parse(storedFilters));
        } else {
            const defaultFilters = {
                startDate: null,
                endDate: null,
                releases: [],
                workItemType: ALL,
                breakDownType: BreakdownType.CURRENT_RELEASE,
                releaseBreakdown: metricData[0]?.name,
                analysisA: metricData[0]?.name,
                analysisB: metricData[1]?.name,
                selectedTypeY1: 'line',
                selectedTypeY2: 'bar',
                selectedStackingY1: "Simple",
                selectedStackingY2: "Simple",
                rollingWindowSizeY1: 1,
                rollingWindowSizeY2: 1,
                selectedStateCategory: selectedStateCategory
            };

            setFilters(defaultFilters);
            setSelectedStartDate(null);
            setSelectedEndDate(null);
            setSelectedReleases([]);
            setSelectedType(ALL);
            setSelectedMetric(metricData[0]?.name);
            setSelectedMetricY1(metricData[0]?.name);
            setSelectedMetricY2(metricData[1]?.name);
            setSelectedBreakdownType(BreakdownType.CURRENT_RELEASE);
            setSelectedTypeY1('line');
            setSelectedTypeY2('bar');
            setSelectedStackingY1('Simple');
            setSelectedStackingY2('Simple');
            setRollingWindowSizeY1(1);
            setRollingWindowSizeY2(1);
        }
    }, [selectedProduct]);

    const updateFilter = (key: string, value: any) => {
        const updatedFilters = { ...filters, [key]: value };
        setFilters(updatedFilters);
    };

    const getFiltersFromStorage = () => {
        let storedFilters = JSON.parse(localStorage.getItem(getFilterStorageKey(selectedProduct)) as string);
        if (storedFilters) {
            setSelectedReleases(storedFilters?.releases)
            setSelectedStartDate(storedFilters?.startDate ? dayjs(storedFilters.startDate) : null);
            setSelectedEndDate(storedFilters?.endDate ? dayjs(storedFilters.endDate) : null);
            setSelectedType(storedFilters?.workItemType)
            setSelectedMetric(storedFilters?.releaseBreakdown || metricData[0]?.name)
            setSelectedMetricY1(storedFilters?.analysisA || metricData[0]?.name)
            setSelectedMetricY2(storedFilters?.analysisB || metricData[1]?.name)
            setSelectedBreakdownType(storedFilters?.breakDownType || BreakdownType.CURRENT_RELEASE)
            setSelectedTypeY1(storedFilters?.selectedTypeY1 || ChartType.LINE)
            setSelectedTypeY2(storedFilters?.selectedTypeY2 || ChartType.BAR)
            setSelectedStackingY1(storedFilters?.selectedStackingY1 || StackingType.SIMPLE)
            setSelectedStackingY2(storedFilters?.selectedStackingY2 || StackingType.SIMPLE)
            setRollingWindowSizeY1(storedFilters?.rollingWindowSizeY1 || 5)
            setRollingWindowSizeY2(storedFilters?.rollingWindowSizeY2 || 5)
            handleFilterChange(storedFilters?.releases, moment(storedFilters?.startDate), moment(storedFilters?.endDate), storedFilters?.workItemType, selectedStateCategory)
            localStorage.setItem(getFilterStorageKey(selectedProduct), JSON.stringify(storedFilters));
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getMetricApi.request(selectedProduct);
                const insights_Response = await getPerformanceInsightsApi.request(selectedProduct);
                const problem_work_item_response = await getPerformanceProblemWorkItemsApi.request(selectedProduct);
                const release_details_response = await getReleaseDetailsApi.request(selectedProduct);
                setPerformanceMetricsData(response?.data?.metrics);
                setMetricData(response?.data?.metrics);
                setInsights(insights_Response?.data?.releases);
                setFilteredInsights(insights_Response?.data?.releases)
                setProblemWorkItems(problem_work_item_response?.data?.releases)
                setFilteredProblemWorkItems(problem_work_item_response?.data?.releases)
                setReleaseDetials(release_details_response?.data?.release_details);
                hasFetchedFilters.current = false;

                const compareTarget = response?.data?.metrics.some(
                    (metric: { target: null; }) => metric.target !== null
                  );
                  setIsCompareTargetVisible(compareTarget);
                  
            } catch (error) {
                console.error("Error fetching metrics:", error);
            }
        };

        fetchData();
    }, [selectedProduct]);

    useEffect(() => {
        if (performanceMetricsData.length > 0 && !hasFetchedFilters.current) {
            getFiltersFromStorage()
            initializeVisibility(performanceMetricsData);
            hasFetchedFilters.current = true;
        }

        const releaseNames: string[] = [];

        performanceMetricsData.forEach((metric: { data: { release: string }[] }) => {
            metric.data.forEach((data: { release: string }) => {
                if (data.release) {
                    releaseNames.push(data.release);
                }
            });
        });

        const uniqueReleases: string[] = Array.from(new Set(releaseNames));

        setReleases(uniqueReleases);
    }, [performanceMetricsData])

    useEffect(() => {
        localStorage.setItem(getFilterStorageKey(selectedProduct), JSON.stringify(filters));
    }, [filters, selectedProduct]);

    const initializeVisibility = (data: Metric[]) => {
        const storedData = localStorage.getItem(getVisibiltyStorageKey(selectedProduct));
        const parsedData = storedData ? JSON.parse(storedData) : null;

        if (parsedData?.visibility?.length) {
            setVisibility(parsedData.visibility);
            setIsTargetEnabled(parsedData.isTargetEnabled ?? true);
        } else {
            const visibilityArray = data.map((metric) => ({
                name: metric.name,
                show: metric.visibility,
            }));

            setVisibility(visibilityArray);
            setIsTargetEnabled(true);

            localStorage.setItem(
                getVisibiltyStorageKey(selectedProduct),
                JSON.stringify({ visibility: visibilityArray, isTargetEnabled: false })
            );
        }
    };

    const getVisibleMetricsCount = () => {
        return metricData.filter((metric: any) =>
            visibility.find((v: { name: string; }) => v.name === metric.name)?.show
        ).length;
    };

    const toggleVisibility = (name: string | "isTargetEnabled") => {
        if (name === "isTargetEnabled") {
            setIsTargetEnabled((prev: any) => {
                const updatedTargetEnabled = !prev;

                localStorage.setItem(
                    getVisibiltyStorageKey(selectedProduct),
                    JSON.stringify({ visibility, isTargetEnabled: updatedTargetEnabled })
                );

                return updatedTargetEnabled;
            });
        } else {
            const existingItem = visibility.find((item: { name: string; }) => item.name === name);

            if (existingItem && !existingItem.show && getVisibleMetricsCount() >= 6) {
                setPendingMetricToggle(name);
                setIsWarningModalVisible(true);
                return;
            }

            setVisibility((prevVisibility: { name: string; show: boolean }[]) => {
                const existingItem = prevVisibility.find((item) => item.name === name);
                let updatedVisibility;

                if (existingItem) {
                    updatedVisibility = prevVisibility.map((item) =>
                        item.name === name ? { ...item, show: !item.show } : item
                    );
                } else {
                    updatedVisibility = [...prevVisibility, { name, show: true }];
                }

                localStorage.setItem(
                    getVisibiltyStorageKey(selectedProduct),
                    JSON.stringify({ visibility: updatedVisibility, isTargetEnabled })
                );

                return updatedVisibility;
            });
        }
    };

    const handleMetricSelection = (metricName: string) => {
        if (pendingMetricToggle) {
            setVisibility((prevVisibility: { name: string; show: boolean }[]) => {
                const updatedVisibility = prevVisibility.map((item) =>
                    item.name === metricName ? { ...item, show: false } : item
                );

                const finalVisibility = updatedVisibility.map((item) =>
                    item.name === pendingMetricToggle ? { ...item, show: true } : item
                );

                localStorage.setItem(
                    getVisibiltyStorageKey(selectedProduct),
                    JSON.stringify({ visibility: finalVisibility, isTargetEnabled })
                );

                return finalVisibility;
            });

            setPendingMetricToggle(null);
            setIsWarningModalVisible(false);
        }
    };

    const handleFilterChange = (selectedReleases: string[], startDate: Moment | null, endDate: Moment | null, selectedType: string, selectedStateCategory: string) => {
        let filteredMetrics = performanceMetricsData;
        let filteredInsights = insights;
        let filteredProblemWorkItems = problemWorkItems;
        setLoading(true)
        if (selectedReleases && selectedReleases?.length > 0) {
            filteredMetrics = filteredMetrics?.map((metric: any) => {
                const filteredData = metric?.data?.filter((data: any) => selectedReleases.includes(data.release));
                return {
                    ...metric,
                    data: filteredData,
                };
            })?.filter((metric: any) => metric?.data?.length > 0);
            filteredInsights = insights?.filter((insight: any) => {
                const releaseVersion = String(insight.release).trim();
                const isMatch = selectedReleases?.some(release => String(release).trim() === releaseVersion);
                return isMatch;
            });
            filteredProblemWorkItems = problemWorkItems?.filter((item: any) =>
                selectedReleases.some(release => String(item.release_version).trim() === String(release).trim())
            );
        }

        if (startDate && startDate?.isValid()) {
            filteredMetrics = filteredMetrics?.map((metric: { data: any[] }) => {
                const filteredData = metric?.data?.filter((data: { date: moment.MomentInput }) => {
                    const metricDate = moment(data.date);
                    if (!metricDate.isValid()) {
                        console.error("Invalid date in metric data:", data.date);
                        return false;
                    }
                    const formattedStartDate = startDate.format('YYYY/MM/DD');
                    const formattedMetricDate = metricDate.format('YYYY/MM/DD');
                    return formattedMetricDate >= formattedStartDate;
                });
                return {
                    ...metric,
                    data: filteredData,
                };
            }).filter((metric: { data: any[] }) => metric.data.length > 0);
        }

        if (endDate && endDate.isValid()) {
            filteredMetrics = filteredMetrics?.map((metric: { data: any[] }) => {
                const filteredData = metric?.data?.filter((data: { date: moment.MomentInput }) => {
                    const metricDate = moment(data.date);
                    if (!metricDate.isValid()) {
                        console.error("Invalid date in metric data:", data.date);
                        return false;
                    }
                    const formattedEndDate = endDate.format('YYYY/MM/DD');
                    const formattedMetricDate = metricDate.format('YYYY/MM/DD');
                    return formattedMetricDate <= formattedEndDate;
                });
                return {
                    ...metric,
                    data: filteredData,
                };
            }).filter((metric: { data: any[] }) => metric.data.length > 0);
        }

        if (selectedType && selectedType !== ALL) {
            filteredMetrics = filteredMetrics?.map((metric: any) => {
                const filteredData = metric.data
                    .map((data: any) => ({
                        ...data,
                        work_item_types: data.work_item_types?.filter(
                            (workItem: { type: string }) => workItem.type === selectedType
                        ),
                    }))
                    .filter((data: any) => data.work_item_types?.length > 0);

                const filteredCurrentRelease = metric.current_release
                    ? metric.current_release
                        .map((release: any) => ({
                            ...release,
                            work_item_types: release.work_item_types?.filter(
                                (workItem: { type: string }) => workItem.type === selectedType
                            ),
                        }))
                        .filter((release: any) => release.work_item_types?.length > 0)
                    : [];
                const filteredTargets = metric.target
                    ? metric.target.filter(
                        (targetItem: { work_item_type: string }) => targetItem.work_item_type === selectedType
                    )
                    : [];
                const updatedMetric: any = {
                    ...metric,
                    data: filteredData,
                    current_release: filteredCurrentRelease,
                };

                if (filteredTargets.length > 0) {
                    updatedMetric.target = filteredTargets;
                } else {
                    delete updatedMetric.target;
                }

                return (filteredData.length > 0 || filteredCurrentRelease.length > 0 || filteredTargets?.length > 0)
                    ? updatedMetric
                    : null;
            }).filter(Boolean);
        }

        if (selectedType === ALL) {
            filteredMetrics = filteredMetrics.map((metric: any) => {
                if (!metric.target || metric.target.length === 0) {
                    return metric;
                }

                let totalValue = 0;

                metric.target.forEach((targetItem: any) => {
                    totalValue += targetItem.value;
                });

                const avgValue = totalValue / metric.target.length;
                const unit = metric.target[0]?.units || "";

                return {
                    ...metric,
                    target: [{ work_item_type: ALL, value: avgValue, units: unit }],
                };
            });
        }

        const stateCategoriesData = getStateCategories(performanceMetricsData);
        if (selectedStateCategory) {
            filteredMetrics = filteredMetrics?.map((metric: any) => {
                const anyStateExists = metric.data?.some((dataItem: any) =>
                    dataItem.work_item_types?.some((type: any) =>
                        type.datasets?.some((d: any) => stateCategoriesData.includes(d.set))
                    )
                );

                if (!anyStateExists) {
                    return metric;
                }

                const filterWorkItemTypesByState = (workItemTypes: any[]) => {
                    return workItemTypes
                        ?.map((type: any) => {
                            const datasets = type.datasets || [];
                            const startIndex = datasets.findIndex(
                                (d: any) => d.set?.toLowerCase() === selectedStateCategory.toLowerCase()
                            );

                            if (startIndex === -1) return null;

                            return {
                                ...type,
                                datasets: datasets.slice(startIndex),
                            };
                        })
                        .filter(Boolean);
                };

                const filteredData = metric.data
                    .map((data: any) => ({
                        ...data,
                        work_item_types: filterWorkItemTypesByState(data.work_item_types),
                    }))
                    .filter((data: any) => data.work_item_types?.length > 0);

                const filteredCurrentRelease = metric.current_release
                    ? metric.current_release
                        .map((release: any) => ({
                            ...release,
                            work_item_types: filterWorkItemTypesByState(release.work_item_types),
                        }))
                        .filter((release: any) => release.work_item_types?.length > 0)
                    : [];

                const filteredTargets = metric.target
                    ? metric.target.filter(
                        (targetItem: { work_item_type: string }) =>
                            targetItem.work_item_type === selectedType
                    )
                    : [];

                const updatedMetric: any = {
                    ...metric,
                    data: filteredData,
                    current_release: filteredCurrentRelease,
                };

                if (filteredTargets.length > 0) {
                    updatedMetric.target = filteredTargets;
                } else {
                    delete updatedMetric.target;
                }

                return (filteredData.length > 0 || filteredCurrentRelease.length > 0 || filteredTargets?.length > 0)
                    ? updatedMetric
                    : null;
            }).filter(Boolean);
        }

        setMetricData([...filteredMetrics]);
        setFilteredInsights(filteredInsights);
        setFilteredProblemWorkItems(filteredProblemWorkItems)
    };

    useEffect(() => {
        if (selectedReleases.length === releases.length) {
            setIsSelectAllChecked(true);
        } else {
            setIsSelectAllChecked(false);
        }
    }, [selectedReleases, releases.length]);

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedReleases(releases);
            setIsSelectAllChecked(true)
            handleFilterChange(releases, selectedStartDate, selectedEndDate, selectedType, selectedStateCategory);
            updateFilter("releases", releases || [])
        } else {
            setSelectedReleases([]);
            setIsSelectAllChecked(false)
            handleFilterChange([], selectedStartDate, selectedEndDate, selectedType, selectedStateCategory);
            updateFilter("releases", [])
        }
    };

    const handleOptionChange = (item: string) => {
        const newSelectedReleases = selectedReleases.includes(item)
            ? selectedReleases.filter((release) => release !== item)
            : [...selectedReleases, item];
        setSelectedReleases(newSelectedReleases);
        handleFilterChange(newSelectedReleases, selectedStartDate, selectedEndDate, selectedType, selectedStateCategory);
        updateFilter("releases", newSelectedReleases || [])
    };

    const renderDropdown = () => {
        return releases?.length ? (
            <>
                <div className="border-bottom-container">
                    <Checkbox
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        checked={isSelectAllChecked}
                    >
                        Select All
                    </Checkbox>
                </div>
                <div className="multi-select-dropdown">
                    {releases.map((item) => (
                        <div key={item} className="dropdown-item">
                            <Checkbox
                                onChange={() => handleOptionChange(item)}
                                checked={selectedReleases.includes(item)}
                            >
                                {item}
                            </Checkbox>
                        </div>
                    ))}
                </div>
            </>
        ) : (
            <div>No releases available</div>
        );
    };

    const handleWorkItemClick = (workItemId: any) => {
        const path = `/work-item?viewMode=WorkItem&workItemId=${workItemId}`;
        navigate(path);
        localStorage.setItem('activeScreenPath', path)
        setWorkItemClicked(true)
        setIsJourneyView(true)
    };

    useEffect(() => {
        if (performanceMetricsData) {
            const uniqueTypes = getUniqueTypes(performanceMetricsData);
            setWorkItemTypes(uniqueTypes);
            const stateCategoriesData = getStateCategories(performanceMetricsData);
            setStateCategories(stateCategoriesData);
        } else {
            setWorkItemTypes([]);
            setStateCategories([]);
        }
    }, [performanceMetricsData]);

    const handleTypeChange = (value: string) => {
        setSelectedType(value);
        handleFilterChange(selectedReleases, selectedStartDate, selectedEndDate, value, selectedStateCategory);
        updateFilter("workItemType", value || null)
    };

    const handleInsightClick = (insight: { insight_id: number; description: string }) => {
        const groupedInsights = (insights ?? []).reduce(
            (
                acc: Record<
                    number,
                    { id: number; description: string; releases: { release: string; release_date: string; priority: number; work_items: { work_item_id: number; weight: number }[] }[] }
                >,
                entry: { insights: { insight_id: number; description: any; priority: any; work_items: { work_item_id: number; weight: number }[] }[]; release: any; release_date: string }
            ) => {
                entry.insights.forEach((insight) => {
                    if (!acc[insight.insight_id]) {
                        acc[insight.insight_id] = {
                            id: insight.insight_id,
                            description: insight.description,
                            releases: [],
                        };
                    }
                    acc[insight.insight_id].releases.push({
                        release: entry.release,
                        release_date: entry.release_date,
                        priority: insight.priority,
                        work_items: insight.work_items,
                    });
                });
                return acc;
            },
            {}
        );
        const insightData = groupedInsights[insight.insight_id];

        let selectedWorkItemIds: number[] = [];

        if (insightData && insightData.releases) {
            const sortedReleases = insightData.releases.sort((a: { priority: number; }, b: { priority: number; }) => b.priority - a.priority);
            let topReleases = sortedReleases.slice(0, 3);
            if (sortedReleases.length >= 4 && topReleases[2].priority === sortedReleases[3].priority) {
                const remainingReleases = sortedReleases.slice(3);
                const samePriorityReleases = remainingReleases.filter((r: { priority: any }) => r.priority === topReleases[2].priority);

                const latestReleaseForThird = samePriorityReleases.sort(
                    (a: { release_date: string | number | Date }, b: { release_date: string | number | Date }) =>
                        new Date(b.release_date).getTime() - new Date(a.release_date).getTime()
                )[0];

                topReleases[2] = latestReleaseForThird;
            }

            let allWorkItems = topReleases.flatMap((r: { work_items: any }) => r.work_items);

            allWorkItems.sort((a: { weight: number }, b: { weight: number }) => b.weight - a.weight);

            selectedWorkItemIds = allWorkItems.slice(0, 5).map((w: { work_item_id: any }) => w.work_item_id);
            const topReleaseVersions = topReleases
                .map((r: { release: string }) => r.release)
                .sort((a: { split: (arg0: string) => { (): any; new(): any; map: { (arg0: NumberConstructor): [any, any]; new(): any; }; }; }, b: { split: (arg0: string) => { (): any; new(): any; map: { (arg0: NumberConstructor): [any, any]; new(): any; }; }; }) => {
                    const [majorA, minorA] = a.split('.').map(Number);
                    const [majorB, minorB] = b.split('.').map(Number);

                    if (majorA === majorB) {
                        return minorA - minorB;
                    }
                    return majorA - majorB;
                })
                .join(",");
            const insightDescription = encodeURIComponent(insight.description);
            setSelectedInsightWorkItems(selectedWorkItemIds.slice(0, 5));
            const path = `/release-comparison?insight=${insightDescription}&releases=${topReleaseVersions}`;
            navigate(path);
            localStorage.setItem('activeScreenPath', path)
        }
    }

    const flattenData = (data: any) => {
        const flattened: any = [];
        data.forEach((metric: any) => {
            const metricName = metric.name;
            const units = metric.units || '';
            metric.data.forEach((release: any) => {
                const releaseName = release.release;
                const releaseDate = release.date;
                const workItemTypes = release.work_item_types || [];

                if (!workItemTypes) {
                    flattened.push({
                        'Release Name': releaseName,
                        'Release Date': releaseDate,
                        'Work Item Type': '',
                        'Metric Name': metricName,
                        'Dataset Name': '',
                        'Metric Value': '',
                        'Metric Units': units,
                    });
                } else {
                    workItemTypes.forEach((workItem: any) => {
                        const workItemType = workItem.type || '';
                        const datasetValue = workItem.value || '';

                        if (!workItem.datasets || workItem.datasets.length === 0) {
                            flattened.push({
                                'Release Name': releaseName,
                                'Release Date': releaseDate,
                                'Work Item Type': workItemType,
                                'Metric Name': metricName,
                                'Dataset Name': workItemType,
                                'Metric Value': datasetValue,
                                'Metric Units': units,
                            });
                        } else {
                            workItem.datasets.forEach((dataset: any) => {
                                const datasetName = dataset.set || '';
                                const datasetValue = dataset.value || '';
                                flattened.push({
                                    'Release Name': releaseName,
                                    'Release Date': releaseDate,
                                    'Work Item Type': workItemType,
                                    'Metric Name': metricName,
                                    'Dataset Name': datasetName,
                                    'Metric Value': datasetValue,
                                    'Metric Units': units,
                                });
                            });
                        }
                    });
                }
            });
        });
        return flattened;
    };

    const convertToCSV = (data: any) => {
        if (!data || !data.length) return "";

        const headers = Object.keys(data[0]).join(",") + "\n";
        const rows = data
            .map((row: any) => Object.values(row).join(","))
            .join("\n");

        return headers + rows;
    };

    const downloadCSV = async () => {
        const data = flattenData(metricData);
        const csv = convertToCSV(data);

        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const fileName = `${selectedProduct}_PerformanceMetrics_${dayjs().format('YYYY_MM_DD')}`
        const link = document.createElement("a");
        link.href = url;
        link.download = `${fileName}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    useEffect(() => {
        if (selectedMetricCard) {
            if (selectedMetricCard === selectedMetricY1 || selectedMetricCard === selectedMetricY2) {
                return;
            }

            if (!selectedMetricY2) {
                setSelectedMetricY2(selectedMetricCard);
            } else {
                setSelectedMetricY1(selectedMetricCard);
            }
        }
    }, [selectedMetricCard]);

    const handleStateCategoriesChange = (value: string) => {
        setSelectedStateCategory(value);
        handleFilterChange(selectedReleases, selectedStartDate, selectedEndDate, selectedType, value);
    };

    const getVisibleMetrics = () => {
        const allMetrics = [...metricData];
        const visibleMetrics = allMetrics.filter(metric =>
            visibility.find((v: { name: string; }) => v.name === metric.name)?.show
        );
        return visibleMetrics;
    };

    return (
        <div className="performance-container">
            <div className="filters">
                <h3 className="card-title mb-2 mt-4 page-heading">
                    Data filters
                </h3>
                {productList?.length > 1 && <div className='mb-2'>
                    <ProductDropdown />
                </div>}

                <div className="filter-item mb-2">
                    <label>Start Date</label>
                    <DatePicker
                        className="w-full"
                        value={selectedStartDate ?? undefined}
                        onChange={(date) => {
                            setSelectedStartDate(date);
                            handleFilterChange(selectedReleases, date, selectedEndDate, selectedType, selectedStateCategory);
                            updateFilter("startDate", date?.format("YYYY-MM-DD") || null)
                        }}
                        format="YYYY-MM-DD"
                        disabledDate={(current) =>
                            current && (current.isAfter(dayjs()) || (selectedEndDate && current.isAfter(dayjs(selectedEndDate))))
                        }
                    />
                </div>
                <div className="filter-item mb-2">
                    <label>End Date</label>
                    <DatePicker
                        className="w-full"
                        value={selectedEndDate ?? undefined}
                        onChange={(date) => {
                            setSelectedEndDate(date);
                            handleFilterChange(selectedReleases, selectedStartDate, date, selectedType, selectedStateCategory);
                            updateFilter("endDate", date?.format("YYYY-MM-DD") || null);
                        }}
                        format="YYYY-MM-DD"
                        disabledDate={(current) =>
                            current?.isBefore(selectedStartDate) || current?.isAfter(dayjs())
                        }
                    />
                </div>
                <DropdownSection
                    label="Releases"
                    value={selectedReleases}
                    options={releases}
                    placeholder={ALL}
                    mode="multiple"
                    filterOption={false}
                    dropdownRender={renderDropdown}
                    maxTagCount="responsive"
                    onChange={(value) => {
                        setSelectedReleases(value);
                        handleFilterChange(value, selectedStartDate, selectedEndDate, selectedType, selectedStateCategory);
                        updateFilter("releases", value || []);
                    }}
                    onClear={() => {
                        setSelectedReleases([]);
                        handleFilterChange([], selectedStartDate, selectedEndDate, selectedType, selectedStateCategory);
                        updateFilter("releases", []);
                    }}
                    style={{ maxWidth: "400px" }}
                />
                <DropdownSection
                    label="Work Item Type"
                    value={selectedType}
                    options={["All", ...workItemTypes]}
                    onChange={handleTypeChange}
                />

                <DropdownSection
                    label="Start from"
                    value={selectedStateCategory && selectedStateCategory.length > 0 ? selectedStateCategory : stateCategories[0]}
                    options={stateCategories}
                    onChange={handleStateCategoriesChange}
                />
            </div>

            <div className="content-area mt-4">
                <h1 className="text-[24px] font-semibold text-center mb-4">{`Performance Dashboard for ${selectedProduct}`}</h1>

                <Card className="shadow-md border rounded-lg">
                    <div className="flex justify-between items-center mt-[-10px]">
                        <div className="flex items-center">
                            <h3 className="card-title">Summary of Key Metrics</h3>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="flex items-center mr-4">
                                <div className="flex items-center mr-3">
                                    <div className="w-5 h-3 border-2 border-blue-600 rounded-sm mr-1"></div>
                                    <span className="text-xs">Performance</span>
                                </div>
                                <div className="flex items-center">
                                    <div className="w-5 h-3 border-2 border-green-500 rounded-sm mr-1"></div>
                                    <span className="text-xs">Health</span>
                                </div>
                            </div>

                            <Dropdown
                                overlay={
                                    <Menu className="w-300px">
                                        {isCompareTargetVisible && (
                                            <>
                                                <Row className='ml-3 mt-2'>
                                                    <Col span={15}>
                                                        <div className="dropDownText">Compare with Target</div>
                                                    </Col>
                                                    <Col span={2}>
                                                        <Switch
                                                            checked={isTargetEnabled as boolean}
                                                            className="dropDownIcon scale-75 ml-2"
                                                            onChange={() => toggleVisibility("isTargetEnabled")}
                                                        />
                                                    </Col>
                                                </Row>
                                                <Menu.Divider />
                                            </>
                                        )}
                                        {metricData.map((metric: { name: string; type: string }) => {
                                            const isVisible = visibility.find((v: { name: string; }) => v.name === metric.name)?.show;
                                            const isPerformanceType = metric.type === 'performance_metric';

                                            return (
                                                <Menu.Item key={metric.name}>
                                                    <div
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleVisibility(metric.name);
                                                        }}
                                                        className="dropdown-list"
                                                    >
                                                        <Badge color={isPerformanceType ? '#2563eb' : '#10b981'} />
                                                        <span className="dropDownText ml-2">{metric.name}</span>
                                                        <span className="dropDownIcon">
                                                            {isVisible ? <EyeOutlined className="ml-8" /> : <EyeInvisibleOutlined className="ml-8" />}
                                                        </span>
                                                    </div>
                                                </Menu.Item>
                                            );
                                        })}
                                    </Menu>
                                }
                                trigger={['click']}
                            >
                                <Button shape="circle" className="action-circle-btn">
                                    <HiViewColumns />
                                </Button>
                            </Dropdown>
                            <Button shape="circle" className="action-circle-btn">
                                <DownloadOutlined onClick={() => downloadCSV()} />
                            </Button>
                        </div>
                    </div>

                    <Row gutter={[16, 16]} className="mt-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 w-full">
                            {getVisibleMetrics().map((metric: {
                                weight: string | null;
                                is_positive: any;
                                name: string;
                                target: string;
                                units: string;
                                data: any;
                                type: string;
                            }) => (
                                <MetricCard
                                    key={metric.name}
                                    name={metric.name}
                                    target={Array.isArray(metric.target) && metric.target.length > 0 ? metric.target[0].value.toString() : null}
                                    units={metric.units}
                                    value={calculateAverage(metric.data, metric.weight)}
                                    metric={metric.data}
                                    is_positive={metric.is_positive}
                                    isTargetEnabled={isTargetEnabled}
                                    setSelectedMetricCard={setSelectedMetricCard}
                                    metricType={metric.type}
                                    pageType={PageType.PERFORMANCE_DASHBOARD}
                                    weight={metric.weight}
                                />
                            ))}
                        </div>
                    </Row>
                </Card>

                <Col span={24}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={15} style={{ display: 'flex', flexDirection: "column" }}>
                            <div className="summary-card trend-card">
                                <MetricChartDropdown
                                    metrics={metricData}
                                    updateFilter={updateFilter}
                                    selectedMetricY1={selectedMetricY1}
                                    setSelectedMetricY1={setSelectedMetricY1}
                                    selectedMetricY2={selectedMetricY2}
                                    setSelectedMetricY2={setSelectedMetricY2}
                                    selectedTypeY1={selectedTypeY1}
                                    setSelectedTypeY1={setSelectedTypeY1}
                                    selectedTypeY2={selectedTypeY2}
                                    setSelectedTypeY2={setSelectedTypeY2}
                                    selectedStackingY1={selectedStackingY1}
                                    setSelectedStackingY1={setSelectedStackingY1}
                                    selectedStackingY2={selectedStackingY2}
                                    setSelectedStackingY2={setSelectedStackingY2}
                                    rollingWindowSizeY1={rollingWindowSizeY1}
                                    setRollingWindowSizeY1={setRollingWindowSizeY1}
                                    rollingWindowSizeY2={rollingWindowSizeY2}
                                    setRollingWindowSizeY2={setRollingWindowSizeY2}
                                    performanceMetricsData={performanceMetricsData}
                                />
                            </div>
                        </Col>

                        <Col xs={24} md={9} style={{ display: 'flex', flexDirection: "column" }}>
                            <div className="summary-card trend-card">
                                <MetricDropdown
                                    metrics={metricData}
                                    updateFilter={updateFilter}
                                    selectedMetric={selectedMetric}
                                    setSelectedMetric={setSelectedMetric}
                                    averageValue={averageValue}
                                    setAverageValue={setAverageValue}
                                    selectedBreakdownType={selectedBreakdownType}
                                    setSelectedBreakdownType={setSelectedBreakdownType}
                                />
                            </div>
                        </Col>
                    </Row>
                </Col>

                <Col span={24}>
                    <Row gutter={[16, 16]} align="top">
                        <Col span={12} style={{ display: 'flex', flexDirection: "column" }}>
                            <div className="summary-card insight-card">
                                <div className="card-header">
                                    <div className="card-title mb-2">
                                        ImproveTeam<sup className="text-[10px]">TM</sup> Insights to Unlock Team Performance
                                    </div>
                                </div>
                                <div className="card-body">
                                    {filteredInsights && Array.isArray(filteredInsights) ? (
                                        (() => {
                                            const allInsights = filteredInsights.flatMap(release => release.insights);

                                            const insightMap = new Map<number, { insight_id: number; description: string; priority: number }>();

                                            allInsights.forEach(insight => {
                                                if (insightMap.has(insight.insight_id)) {
                                                    insightMap.get(insight.insight_id)!.priority += insight.priority;
                                                } else {
                                                    insightMap.set(insight.insight_id, { ...insight });
                                                }
                                            });
                                            const sortedInsights = Array.from(insightMap.values())
                                                .sort((a, b) => b.priority - a.priority);

                                            const topWeightages = Array.from((sortedInsights.map(insight => insight.priority)))
                                                .sort((a, b) => b - a)
                                                .slice(0, 3);

                                            const topInsights = sortedInsights.filter(insight => topWeightages.includes(insight.priority));

                                            return topInsights.length > 0 ? (
                                                <ul>
                                                    {topInsights.map((insight, index) => (
                                                        <li className="insight-item cursor-pointer transition-transform duration-200 hover:translate-x-1 hover:text-blue-700"
                                                            key={index}
                                                            onClick={() => handleInsightClick(insight)}
                                                        >
                                                            -  {insight.description}
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="no-insights">No insights available.</p>
                                            );
                                        })()
                                    ) : (
                                        <p className="no-insights">No insights available.</p>
                                    )}
                                </div>
                            </div>
                        </Col>

                        <Col span={12}>
                            <div className="summary-card insight-card">
                                <div className="card-header">
                                    <div className="card-title mb-2">Overall Problem Work Items</div>
                                </div>
                                <div className="card-body">
                                    {filteredProblemWorkItems && Array.isArray(filteredProblemWorkItems) ? (
                                        (() => {
                                            const allWorkItems = filteredProblemWorkItems.flatMap(release => release.work_items);

                                            const sortedWorkItems = Array.from(allWorkItems.values())
                                                .sort((a, b) => b.work_item_priority - a.work_item_priority);

                                            const topPriorities = Array.from(sortedWorkItems.map(item => item.work_item_priority))
                                                .sort((a, b) => b - a)
                                                .slice(0, 5);
                                            const topWorkItems = sortedWorkItems.filter(item =>
                                                topPriorities.includes(item.work_item_priority)
                                            );

                                            return topWorkItems.length > 0 ? (
                                                <ul>
                                                    {topWorkItems.map((item, index) => (
                                                        <li className="work-item" key={index} onClick={() => handleWorkItemClick(item.work_item_id)}>
                                                            {item.work_item_id}: {item.title}
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="no-work-items">No work items available.</p>
                                            );
                                        })()
                                    ) : (
                                        <p className="no-work-items">No work items available.</p>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Col>
            </div>

            <Modal
                open={isWarningModalVisible}
                onCancel={() => {
                    setIsWarningModalVisible(false);
                    setPendingMetricToggle(null);
                }}
                footer={null}
            >
                <p>You can display a maximum of 6 metrics at once. Please select a metric to hide in order to show your new selection:</p>

                <div className="mt-4 max-h-60">
                    {getVisibleMetrics().map((metric: any) => {
                        const isPerformanceType = metric.type === 'performance_metric';

                        return (
                            <div
                                key={metric.name}
                                className="p-2 mb-2 border rounded cursor-pointer hover:bg-gray-50 flex items-center"
                                onClick={() => handleMetricSelection(metric.name)}
                            >
                                <input type='checkbox' style={{ cursor: 'pointer' }} color={isPerformanceType ? '#2563eb' : '#10b981'} />
                                <span className="ml-2">{metric.name}</span>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-11 text-right">
                    <Button
                        onClick={() => {
                            setIsWarningModalVisible(false);
                            setPendingMetricToggle(null);
                        }}
                    >
                        Cancel
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default Performance;